import { useState, useEffect } from "react";
import { privacyAPI, authAPI } from "@/lib/api";
import { useTranslation } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Shield, Download, Trash2, FileText, Eye, Brain, Mail, BarChart3, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const PURPOSES = [
  { key: "ai_matching", icon: Brain, labelKey: "priv.ai_matching", descKey: "priv.ai_matching_desc" },
  { key: "profile_visibility", icon: Eye, labelKey: "priv.profile_visibility", descKey: "priv.profile_visibility_desc" },
  { key: "analytics", icon: BarChart3, labelKey: "priv.analytics", descKey: "priv.analytics_desc" },
  { key: "marketing_email", icon: Mail, labelKey: "priv.marketing_email", descKey: "priv.marketing_email_desc" },
];

export default function PrivacyCenter({ user }) {
  const { t } = useTranslation();
  const [consents, setConsents] = useState({});
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cRes, sRes] = await Promise.all([privacyAPI.getConsents(), privacyAPI.getDataSummary()]);
        setConsents(cRes.data.consents || {});
        setSummary(sRes.data);
      } catch { }
      finally { setLoading(false); }
    })();
  }, []);

  const toggleConsent = async (purpose, currentlyGranted) => {
    const newStatus = currentlyGranted ? "withdrawn" : "granted";
    try {
      await privacyAPI.updateConsent(purpose, newStatus);
      setConsents(prev => ({ ...prev, [purpose]: { ...prev[purpose], granted: !currentlyGranted, timestamp: new Date().toISOString() } }));
      toast.success(currentlyGranted ? t("priv.consent_withdrawn") : t("priv.consent_granted"));
    } catch { toast.error("Error"); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await authAPI.exportData();
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `staffera-my-data-${new Date().toISOString().split("T")[0]}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(t("priv.export_success"));
    } catch { toast.error("Error"); }
    finally { setExporting(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="privacy-center">
      <div className="flex items-center gap-2 mb-2">
        <Shield className="w-5 h-5 text-gold-500" />
        <h2 className="font-sans font-semibold text-lg text-slate-900">{t("priv.title")}</h2>
      </div>

      {/* Data Summary */}
      {summary && (
        <Card className="bg-white border-slate-200" data-testid="data-summary-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-sans">{t("priv.your_data")}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="text-xs font-body text-slate-500">{t("priv.member_since")}: {summary.account?.created_at ? new Date(summary.account.created_at).toLocaleDateString() : "—"}</div>
            <div className="flex flex-wrap gap-1.5">
              {(summary.data_categories || []).map((cat, i) => (
                <Badge key={i} className="bg-slate-100 text-slate-600 border-slate-200 text-[11px] font-normal">{cat}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Consents */}
      <Card className="bg-white border-slate-200" data-testid="consents-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-sans">{t("priv.manage_consents")}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {PURPOSES.map(({ key, icon: Icon, labelKey, descKey }) => {
            const c = consents[key] || { granted: false };
            return (
              <div key={key} className="flex items-start gap-3" data-testid={`consent-${key}`}>
                <Icon className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-slate-800 font-body">{t(labelKey)}</span>
                    <Switch checked={c.granted} onCheckedChange={() => toggleConsent(key, c.granted)} data-testid={`consent-toggle-${key}`} />
                  </div>
                  <p className="text-xs text-slate-400 font-body mt-0.5">{t(descKey)}</p>
                  {c.timestamp && <p className="text-[10px] text-slate-300 font-body mt-0.5">{c.granted ? t("priv.granted_on") : t("priv.withdrawn_on")}: {new Date(c.timestamp).toLocaleDateString()}</p>}
                </div>
              </div>
            );
          })}
          <p className="text-[10px] text-slate-400 font-body border-t border-slate-100 pt-2">
            <AlertTriangle className="w-3 h-3 inline mr-1" />{t("priv.consent_note")}
          </p>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-sans">{t("priv.your_rights")}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <Button variant="outline" size="sm" className="w-full justify-start text-sm font-body" onClick={handleExport} disabled={exporting} data-testid="export-data-btn">
            <Download className="w-3.5 h-3.5 mr-2" />{exporting ? t("priv.exporting") : t("priv.export_data")}
          </Button>
          <a href="/privacy" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" className="w-full justify-start text-sm font-body">
              <FileText className="w-3.5 h-3.5 mr-2" />{t("priv.view_policy")}
            </Button>
          </a>
          <p className="text-[10px] text-slate-400 font-body pt-1">{t("priv.contact_dpo")}: admin@staffera.eu</p>
        </CardContent>
      </Card>
    </div>
  );
}
